import LoadingOverlay from 'components/loading-overlay/loading-overlay';
import React from 'react';
import Validation from 'components/form/validation/validation';
import { ApplicationManagerClient } from 'clients/application-manager-client';
import { Controller, useForm } from 'react-hook-form';
import { Button, Grid, Modal, Typography } from '@mui/material';
import { CircularLoadingButton } from 'components/circular-loading-button/circular-loading-button';
import { CustomerId } from 'interfaces/RecordTypes';
import { buildQaIdProp, buildQaId } from 'utils/build-qa-id';
import { ControlledText } from 'components/react-hook-form-fields';
import { fetchTeamsForCustomer } from 'actions/action-lookups';
import { getModalStyle } from 'services/utils/styles-service';
import { nameOfFactory } from 'utils/types-util';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { NumberOption } from 'interfaces/forms/types';
import { USER_TEAM_FILTER_TYPES } from 'constants/index';
import { ReactSelect } from 'components/form/field/react_select';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { IGetServiceGroupsResponseItem } from 'models/application-manager/users/IGetServiceGroupsResponse';

import { IFormFields, ITeamModalProps, IRequestPayload, IRequestPayloadEdit } from './types';
import { styles } from './team-modal.styles';
import { logger } from '../../../../../winston-logger';

const getFieldName = nameOfFactory<IFormFields>();
const qaIdProp = buildQaIdProp('add-user-group-form');
const qaId = buildQaId('add-user-group-form');

const requiredErrorMsg = 'Required';
const getQaId = buildQaId('application-manager.users.team-modal', '.');

export const TeamModal: React.FC<ITeamModalProps> = (props: ITeamModalProps): JSX.Element => {
  const classes: any = styles();

  // #region component state
  const form: any = useForm<any>({
    defaultValues: {
      name: '',
      service_groups: [],
      clinics: [],
    },
  });

  const {
    formState: { errors },
  } = form;

  const dispatch = useDispatch();

  const userTeams = useTypedSelector(state => state.lookups.lookupUserTeams);
  const roles = useTypedSelector(state => state.lookups.roles);
  const customers = useTypedSelector(state => state.filters.customers);
  const clinics = useTypedSelector(state => state.lookups.customerClinics);

  const users = useTypedSelector(state => state.lookups.users);

  const [loading, setLoading] = React.useState<boolean>(true);
  const [clinicOptions, setClinicOptions] = React.useState<NumberOption[] | undefined>();

  const [serviceGroupsPerCustomer, setServiceGroupsPerCustomer] = React.useState<
    Record<CustomerId, NumberOption[]> | undefined
  >(undefined);
  const [permissionOptions, setPermissionOptions] = React.useState<NumberOption[] | undefined>();
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const serviceGroupsForCustomer =
    serviceGroupsPerCustomer && serviceGroupsPerCustomer[props.selectedCustomerId]
      ? serviceGroupsPerCustomer[props.selectedCustomerId]
      : [];

  const teamBeingEdited = !!props.editingTeam;

  // #endregion

  React.useEffect(() => {
    if (props.open) {
      setLoading(true);

      (async () => {
        // Load permissions
        try {
          const permissions = await ApplicationManagerClient.getPermissionsAssignablePerCustomer();
          setPermissionOptions(
            permissions.data.map(x => {
              return { label: x.display_name, value: x.id };
            }),
          );
        } catch (error) {
          logger.error(error);
          dispatch(notifyError('Error fetching permissions'));
        }

        // Load service groups
        try {
          // Tech debt: we should load all at once for all sites.
          const allPromises = customers.map(site => {
            const result = new Promise<{
              customerId: CustomerId;
              serviceGroups: IGetServiceGroupsResponseItem[];
            }>(async resolve => {
              const apiResponse = await ApplicationManagerClient.getCustomerServiceGroups(site.id);
              resolve({
                customerId: site.id,
                serviceGroups: apiResponse.data,
              });
            });
            return result;
          });

          const serviceGroupPromises = await Promise.all(allPromises);
          const serviceGroups: Record<CustomerId, NumberOption[]> = serviceGroupPromises.reduce<
            Record<CustomerId, NumberOption[]>
          >((acc, curr) => {
            acc[curr.customerId] = curr.serviceGroups.map<NumberOption>(sg => {
              return {
                label: sg.display_name,
                value: sg.id,
              };
            });
            return acc;
          }, {});
          setServiceGroupsPerCustomer(serviceGroups);
        } catch (error) {
          logger.error(error);
          dispatch(notifyError('Error fetching service groups'));
        }

        // Load clinics
        try {
          const results = await ApplicationManagerClient.getAllCustomerClinics(
            props.selectedCustomerId,
          );
          setClinicOptions(
            results.data
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(x => {
                return { label: x.name, value: x.id };
              }),
          );
        } catch (error) {
          logger.error(error);
          dispatch(notifyError('Error fetching clinics'));
        }

        setLoading(false);
      })();
    }
  }, [props.open, props.selectedCustomerId, props.editingTeam]);

  // #region use effects
  React.useEffect(() => {
    if (permissionOptions && props.editingTeam?.teamId) {
      setLoading(true);
      if (props.editingTeam.teamId) {
        const team = userTeams.find(t => t.teamId === props.editingTeam?.teamId);
        form.setValue(getFieldName('name'), team.teamName);
        const teamServiceGroups = team.filters.filter(
          (f: any) => f.resource_type === USER_TEAM_FILTER_TYPES.SERVICE_GROUP,
        );
        const teamClinics = team.filters.filter(
          (f: any) => f.resource_type === USER_TEAM_FILTER_TYPES.CLINIC,
        );
        const serviceGroupsForForm = teamServiceGroups.map((s: any) => ({
          value: s.resource_id,
          label:
            serviceGroupsForCustomer &&
            serviceGroupsForCustomer.length > 0 &&
            serviceGroupsForCustomer?.find(c => c.value === s.resource_id)
              ? serviceGroupsForCustomer?.find(c => c.value === s.resource_id)?.label
              : '-',
        }));
        const clinicsForForm = teamClinics.map((s: any) => ({
          value: s.resource_id,
          label:
            clinicOptions &&
            clinicOptions.length > 0 &&
            clinicOptions?.find(c => c.value === s.resource_id)
              ? clinicOptions?.find(c => c.value === s.resource_id)?.label
              : '-',
        }));
        form.setValue(getFieldName('service_groups'), serviceGroupsForForm);
        form.setValue(getFieldName('clinics'), clinicsForForm);
      }
    }
  }, [props.open, props.editingTeam, permissionOptions, props.selectedCustomerId]);
  // #endregion

  // #region helper functions
  /**
   * When adding a user
   */
  const submitAddUser = async (submittedValues: IFormFields): Promise<void> => {
    try {
      const payload: IRequestPayload = {
        name: submittedValues.name || '',
        clinics: submittedValues.clinics ? submittedValues.clinics.map(c => c.value) : [],
        service_groups: submittedValues.service_groups
          ? submittedValues.service_groups.map(s => s.value)
          : [],
        customer_id: props.selectedCustomerId,
      };

      const response = ApplicationManagerClient.addNewUserTeam(
        payload.name,
        payload.clinics,
        payload.service_groups,
        payload.customer_id,
      );

      await response;

      const response2 = ApplicationManagerClient.getUserTeams();
      await response2;

      dispatch(notifySuccess('Added Team'));
      onSuccess();
      dispatch(fetchTeamsForCustomer(props.selectedCustomerId));
    } catch (error) {
      logger.error(error);
      dispatchError(error, 'Error adding Team');
    } finally {
      dispatch(fetchTeamsForCustomer(props.selectedCustomerId));

      setSubmitting(false);
    }
  };

  /**
   * When editing an existing user
   */
  const submitEditUser = async (submittedValues: IFormFields): Promise<void> => {
    try {
      if (props.editingTeam?.teamId) {
        const payload: IRequestPayloadEdit = {
          id: Number(props.editingTeam?.teamId),
          name: submittedValues.name || '',
          clinics: submittedValues.clinics ? submittedValues.clinics.map(c => c.value) : [],
          service_groups: submittedValues.service_groups
            ? submittedValues.service_groups.map(s => s.value)
            : [],
        };

        const response = ApplicationManagerClient.updateUserTeam(
          payload.id, // todo: put in id
          payload.clinics,
          payload.service_groups,
        );

        await response;

        dispatch(notifySuccess('Updated Group'));
        dispatch(fetchTeamsForCustomer(props.selectedCustomerId));

        onSuccess();
      } else {
        logger.error('undefined team id');
      }
    } catch (error) {
      logger.error(error);
      if (error instanceof Error) {
        dispatchError(error, error.message ?? 'Error editing user');
      }
    } finally {
      dispatch(fetchTeamsForCustomer(props.selectedCustomerId));

      setSubmitting(false);
    }
  };

  const onSuccess = (response?: any) => {
    form.reset();
    props.onSuccess(response);
  };

  const onSubmit = async (submittedValues: IFormFields): Promise<void> => {
    setSubmitting(true);
    if (!teamBeingEdited) {
      await submitAddUser(submittedValues);
    } else {
      await submitEditUser(submittedValues);
    }
  };

  const getModalTitle = (): string => {
    if (props.editingTeam != null) {
      const user = users.find(x => x.id === props.editingTeam?.teamId);
      if (user) {
        return `Edit ${user?.display_name}`;
      }
    }
    return 'Add Team';
  };

  const dispatchError = (error: any, fallbackMessage: string): void => {
    const errorData = error?.response?.data || {};
    const errorMsg: string = errorData.validationError || errorData.message || fallbackMessage;
    dispatch(notifyError(errorMsg));
  };

  // #endregion

  // #region render functions

  // #endregion

  return (
    <Modal
      open={props.open}
      data-qa-id={getQaId('wrapper')}
      onBackdropClick={() => {
        props.onCancel();
      }}
    >
      <div style={getModalStyle()} className={classes.modal}>
        <Grid container direction="column" spacing={2}>
          {/* Title */}
          <Grid item xs="auto">
            <Typography className={classes.title} data-qa-id={getQaId('title')}>
              {getModalTitle()}
            </Typography>
          </Grid>

          {/* Form */}
          <Grid item xs="auto" data-qa-id={getQaId('form')}>
            <Grid container spacing={1}>
              <>
                <Grid item xs={4}>
                  <ControlledText
                    defaultValue={null}
                    name={getFieldName('name')}
                    control={form.control}
                    label="Name *"
                    disabled={!!props.editingTeam}
                    validations={{ required: true }}
                    inputMetaData={{ touched: Boolean(errors.name), error: 'Required' }}
                    {...qaIdProp('lastName')}
                  />
                </Grid>
              </>

              <Grid item xs={6}>
                <Controller
                  name={getFieldName('service_groups')}
                  control={form.control}
                  rules={{ required: false }}
                  render={(ctrlProps: any) => {
                    return (
                      <>
                        <LoadingOverlay open={loading} />
                        <ReactSelect
                          label="Service Groups *"
                          isMulti
                          value={ctrlProps?.field?.value}
                          fields={serviceGroupsForCustomer}
                          handleOnChange={(value: NumberOption) =>
                            ctrlProps?.field?.onChange(value)
                          }
                          fullWidth
                        />
                        <Validation
                          touched={form.formState.errors.customerBranch}
                          error={requiredErrorMsg}
                        />
                      </>
                    );
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name={getFieldName('clinics')}
                  control={form.control}
                  rules={{ required: false }}
                  render={(ctrlProps: any) => {
                    return (
                      <>
                        <LoadingOverlay open={loading} />
                        <ReactSelect
                          label="Clinics *"
                          isMulti
                          value={ctrlProps?.field?.value}
                          fields={clinicOptions}
                          handleOnChange={(value: NumberOption) =>
                            ctrlProps?.field?.onChange(value)
                          }
                          fullWidth
                        />
                        <Validation
                          touched={form.formState.errors.customerBranch}
                          error={requiredErrorMsg}
                        />
                      </>
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container>
                  <Grid container justifyContent="flex-end" spacing={2} xs={6}>
                    <Grid item>
                      <Button
                        data-qa-id={getQaId('button.cancel')}
                        onClick={() => {
                          form.reset();
                          props.onCancel();
                        }}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <CircularLoadingButton
                        data-qa-id={getQaId('button.save')}
                        buttonLabel="Save"
                        onClick={form.handleSubmit((submittedValues: any) =>
                          onSubmit(submittedValues),
                        )}
                        isLoading={submitting}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <LoadingOverlay open={loading} />
      </div>
    </Modal>
  );
};
